import {Injectable} from '@angular/core';

import {Observable, of} from 'rxjs';

import {LocalStorage, SessionStorageService} from 'ngx-webstorage';
import {map, share, tap, finalize, filter, catchError} from 'rxjs/operators';
import {HttpService, autoSubscribe, retryOnError, tapOnError, connectToSocket, JsonObject, AbstractModel, JsonProperty} from '../../core/public-api';
import {User} from './user';
import {HttpErrorService} from '../../core/data/http-error.service';
import {AppStateStore} from 'projects/desktop/src/app/data/app-state.store';
import {getUserAvatar} from 'projects/app-misc/src/app/modules/profile/utils/profile.utils';
import {RoleAccess} from './role-access';
import {environment} from 'projects/desktop/src/environments/environment';


@JsonObject('model.user.LoginResult')
export class LoginResult extends AbstractModel {
  @JsonProperty(Boolean, 'reason')
  reason: string;

  @JsonProperty(Boolean, 'success')
  success: boolean;

  @JsonProperty(Boolean, 'user')
  user: User;
}


@Injectable({
  providedIn: 'root'
})
export class LoginService {

  // @LocalStorage()
  // private apiKey: string;

  @LocalStorage('currentUser')
  pCurrentUser: User;

  set currentUser(v: User) {
    this.pCurrentUser = v;
    this.storageService
      .store('currentUser', v);
  }

  get currentUser(): User {
    return this.pCurrentUser
  }

  constructor(
    protected http: HttpService,
    protected httpErrorService: HttpErrorService,
    protected appStateStore: AppStateStore,
    private storageService: SessionStorageService
  ) {
    httpErrorService
      .error$
      .pipe(
        filter(error => error.status === 403)
      )
      .subscribe(error => {
        this.currentUser = null;
        this.appStateStore.updateAutorizedStatus(false);
      })
  }


  checkStatus(): Observable<boolean> {
    return this.http.doHttpGet<any>(`/login/status`)
      .pipe(
        retryOnError(3, 300),
        map(data => data || {
          success: false
        }),
        tap(data => {
          this.getRolesAndUpdateStatusAndUser(data)
        }),
        tapOnError(error => {
          this.appStateStore.updateAutorizedStatus(false)
        }),
        catchError(error => {
          this.appStateStore.updateInitializedStatus(true);
          console.log('error')
          return of([])
        }),
        finalize(() => {
          this.appStateStore.updateInitializedStatus(true)
        }),
        map(data => data.success),
        autoSubscribe()
      )
  }

  doAuth(username: string, password: string): Observable<LoginResult> {

    let auth$: Observable<any>
    auth$ = this.http.doHttpPost<any>('/login/auth', {
      username,
      password
    })

    return auth$.pipe(
      tap(data => {
        console.log(data)
        this.getRolesAndUpdateStatusAndUser(data)
      }),
      tapOnError(error => {
        this.appStateStore.updateAutorizedStatus(false)
      })
    )
  }

  loginAs(username: string, password: string): Observable<LoginResult> {

    let auth$: Observable<any>
    auth$ = this.http.doHttpPost<any>('/login/auth-admin', {
      username,
      password
    })

    return auth$.pipe(
      tap(data => {
        this.getRolesAndUpdateStatusAndUser(data)
      }),
      tapOnError(error => {
        this.appStateStore.updateAutorizedStatus(false)
      })
    )
  }

  getRolesAndUpdateStatusAndUser(data: any) {
    if (data.success) {
      this.getRolesAccess().subscribe(accessArr => {
        const rolesAccessMap = new Map<string, string[]>(
          accessArr.map(access => {
            return [access.path, access.roles.map(role => role.name)]
          }),
        );
        this.appStateStore.updateRolesMap(rolesAccessMap)

        if (data.user) {
          this.currentUser = this.http.fromJson(data.user, User);
          this.appStateStore.updateCurrentUser(this.currentUser)
          this.appStateStore.updateUserProfile(getUserAvatar(this.currentUser))
        }

        this.appStateStore.updateAutorizedStatus(data.success)
      })
    } else {
      this.appStateStore.updateAutorizedStatus(data.success)
    }
  }

  doAuthWithPinCode(username: string, pinCode: string): Observable<boolean> {

    let auth$: Observable<any>
    auth$ = this.http.doHttpPost<any>('/login/auth-with-pin', {
      username,
      pinCode
    })

    return auth$.pipe(
      map(data => data || {
        success: false
      }),
      tap(data => {
        if (data.user) {
          this.currentUser = this.http.fromJson(data.user, User);
          this.appStateStore.updateCurrentUser(this.currentUser)
          this.appStateStore.updateUserProfile(getUserAvatar(this.currentUser))
          // this.apiKey = this.currentUser.apiKey;
        }

        this.appStateStore.updateAutorizedStatus(data.success)
      }),
      tapOnError(error => {
        this.appStateStore.updateAutorizedStatus(false)
      }),
      map(data => data.success)
    )
  }

  logout(): Observable<void> {
    return this
      .http
      .doHttpGet<void>(`/logout`)
      .pipe(
        share(),
        autoSubscribe()
      );
  }

  changePassword(oldPassword: string, password: string): Observable<void> {
    return this.http.postData('/login/change-password', {
      password,
      oldPassword
    }, Object)
  }

  changePin(oldPinCode: string, pinCode: string): Observable<void> {
    return this.http.postData('/login/change-pin-code', {
      pinCode,
      oldPinCode
    }, Object)
  }

  checkQBStatus(): Observable<boolean> {
    return this.http.doHttpGet<any>(`/login/qb/status`)
  }

  getRolesAccess(): Observable<RoleAccess[]> {
    return this.http.getData(`/page-view/list`, RoleAccess)
  }

  switchSystem(sysToSwitch: string) {
    if (sysToSwitch === 'Seattle') {
      location.assign(environment.systemSeattleUrl)
    }
    if (sysToSwitch === 'California') {
      location.assign(environment.systemCaliforniaUrl)
    }
  }
}
